import React from 'react';
import { SimpleGrid, Box, Flex, Text, Icon, Skeleton, HStack } from '@chakra-ui/react';
import {
  Buildings,
  CurrencyDollar,
  WarningCircle,
  ArrowCircleDown,
  ArrowCircleUp,
  Package,
} from 'phosphor-react';
import { formatCurrency } from '../../../../../utils/FormatFunctions';
import { IResumoCalculado, IDivergencia } from '../constants';
import { IEmpresasPorTipo } from '../../../../../hooks/useFiscalContabil';

export type KpiFilterType = 'entradas' | 'saidas' | 'servicos' | 'divergencias';

interface KpiCardsProps {
  resumo: IResumoCalculado;
  divergencias: IDivergencia;
  isLoading: boolean;
  totalEmpresas: number;
  empresasPorTipo?: IEmpresasPorTipo;
  selectedFilters?: KpiFilterType[];
  /**
   * Cards de tipo desabilitados quando o drawer ja filtra por `tipo` —
   * evita estado visual selecionado sem efeito real (drawer.tipo vence sobre KPI).
   */
  disabledFilters?: KpiFilterType[];
  // eslint-disable-next-line no-unused-vars
  onFilterToggle?: (filter: KpiFilterType) => void;
}

interface EmpresaStats {
  count: number;
  percentage: number;
}

const getCursor = (interactive: boolean, isDisabled: boolean): string => {
  if (interactive) return 'pointer';
  if (isDisabled) return 'not-allowed';
  return 'default';
};

interface KpiCardProps {
  label: string;
  value: string | number;
  helpText?: string;
  icon: React.ElementType;
  iconColor: string;
  iconBg: string;
  borderColor: string;
  isLoading?: boolean;
  valueColor?: string;
  isHighlight?: boolean;
  divergenciasCount?: number;
  totalValue?: number;
  empresaStats?: EmpresaStats;
  isSelectable?: boolean;
  isSelected?: boolean;
  isDisabled?: boolean;
  selectedColor?: 'blue' | 'red';
  onClick?: () => void;
}

function KpiCard({
  label,
  value,
  helpText,
  icon,
  iconColor,
  iconBg,
  borderColor,
  isLoading,
  valueColor = 'gray.800',
  isHighlight,
  divergenciasCount,
  totalValue,
  empresaStats,
  isSelectable,
  isSelected,
  isDisabled,
  selectedColor = 'blue',
  onClick,
}: KpiCardProps) {
  const hasDivergencias = divergenciasCount !== undefined && divergenciasCount > 0;
  const selectedBg = selectedColor === 'red' ? 'red.50' : 'blue.50';
  const selectedRing = selectedColor === 'red' ? 'red.300' : 'blue.300';
  const interactive = isSelectable && !isDisabled;
  return (
    <Box
      bg="white"
      p={{ base: 2, md: 3 }}
      borderRadius="xl"
      boxShadow="sm"
      borderLeft="4px"
      borderColor={borderColor}
      transition="all 0.2s"
      opacity={isDisabled ? 0.5 : 1}
      title={isDisabled ? 'Filtro de tipo já aplicado no drawer' : undefined}
      _hover={
        interactive
          ? {
              boxShadow: 'md',
              transform: 'translateY(-1px)',
              cursor: 'pointer',
              borderLeftWidth: '6px',
            }
          : undefined
      }
      position="relative"
      onClick={interactive ? onClick : undefined}
      cursor={getCursor(!!interactive, !!isDisabled)}
      {...(isHighlight && {
        bg: 'red.50',
        borderColor: 'red.400',
      })}
      {...(isSelected && {
        bg: selectedBg,
        borderLeftWidth: '6px',
        boxShadow: 'md',
        ring: '2px',
        ringColor: selectedRing,
      })}
    >
      <Flex align="center" justify="space-between" mb={1}>
        <HStack spacing={2}>
          <Flex
            align="center"
            justify="center"
            w={7}
            h={7}
            borderRadius="md"
            bg={iconBg}
            flexShrink={0}
          >
            <Icon as={icon} boxSize={4} color={iconColor} />
          </Flex>
          <Text fontSize="xs" color="gray.500" fontWeight="medium">
            {label}
          </Text>
        </HStack>
      </Flex>
      {isLoading && <Skeleton height="24px" mt={1} />}
      {!isLoading && hasDivergencias && totalValue !== undefined && (
        <Flex align="baseline" gap={1} ml={9}>
          <Text fontSize={{ base: 'md', md: 'lg' }} fontWeight="bold" color="red.600">
            {divergenciasCount}
          </Text>
          <Text fontSize="xs" color="gray.400">
            de
          </Text>
          <Text fontSize={{ base: 'md', md: 'lg' }} fontWeight="bold" color={valueColor}>
            {totalValue}
          </Text>
        </Flex>
      )}
      {!isLoading && !(hasDivergencias && totalValue !== undefined) && (
        <Text fontSize={{ base: 'md', md: 'lg' }} fontWeight="bold" color={valueColor} ml={9}>
          {value}
        </Text>
      )}
      <Flex justify="space-between" align="center" flexWrap="wrap" gap={1} ml={9}>
        {helpText && (
          <Text fontSize="10px" color="gray.400" flex="1" minW="0">
            {helpText}
          </Text>
        )}
        {empresaStats && (
          <Text
            fontSize="10px"
            color="gray.500"
            whiteSpace="nowrap"
            title={`${empresaStats.count} empresas (${empresaStats.percentage}%)`}
          >
            {empresaStats.count}emp|{empresaStats.percentage}%
          </Text>
        )}
      </Flex>
    </Box>
  );
}

export function KpiCards({
  resumo,
  divergencias,
  isLoading,
  totalEmpresas,
  empresasPorTipo,
  selectedFilters = [],
  disabledFilters = [],
  onFilterToggle,
}: KpiCardsProps) {
  const hasDivergencias = divergencias.quantidade > 0;

  const calcPercentage = (value: number, total: number) => {
    if (!total || total <= 0) return 0;
    const pct = Math.round((value / total) * 100);
    if (!Number.isFinite(pct)) return 0;
    return Math.min(100, Math.max(0, pct));
  };

  const entradasStats = empresasPorTipo
    ? {
        count: empresasPorTipo.entradas,
        percentage: calcPercentage(empresasPorTipo.entradas, empresasPorTipo.total),
      }
    : undefined;

  const saidasStats = empresasPorTipo
    ? {
        count: empresasPorTipo.saidas,
        percentage: calcPercentage(empresasPorTipo.saidas, empresasPorTipo.total),
      }
    : undefined;

  const servicosStats = empresasPorTipo
    ? {
        count: empresasPorTipo.servicos,
        percentage: calcPercentage(empresasPorTipo.servicos, empresasPorTipo.total),
      }
    : undefined;

  const divergenciasStats =
    divergencias.totalEmpresas !== undefined && totalEmpresas > 0
      ? {
          count: divergencias.totalEmpresas,
          percentage: calcPercentage(divergencias.totalEmpresas, totalEmpresas),
        }
      : undefined;

  return (
    <SimpleGrid columns={{ base: 2, md: 3, xl: 6 }} spacing={{ base: 3, md: 4 }} mb={6}>
      <KpiCard
        label="Total Empresas"
        value={totalEmpresas}
        helpText={`${resumo.totalRegistros} registros`}
        icon={Buildings}
        iconColor="blue.500"
        iconBg="blue.50"
        borderColor="blue.400"
        isLoading={isLoading}
      />

      <KpiCard
        label="Valor Contabil"
        value={formatCurrency(resumo.valorContabilTotal)}
        helpText="Soma valor_contabil"
        icon={CurrencyDollar}
        iconColor="teal.500"
        iconBg="teal.50"
        borderColor="teal.400"
        isLoading={isLoading}
      />

      <KpiCard
        label="Entradas"
        value={resumo.totalEntradas}
        helpText={formatCurrency(resumo.valorEntradas)}
        icon={ArrowCircleDown}
        iconColor="green.500"
        iconBg="green.50"
        borderColor="green.400"
        isLoading={isLoading}
        divergenciasCount={resumo.divergenciasEntradas}
        totalValue={resumo.totalEntradas}
        empresaStats={entradasStats}
        isSelectable
        isSelected={selectedFilters.includes('entradas')}
        isDisabled={disabledFilters.includes('entradas')}
        onClick={() => onFilterToggle?.('entradas')}
      />

      <KpiCard
        label="Saidas"
        value={resumo.totalSaidas}
        helpText={formatCurrency(resumo.valorSaidas)}
        icon={ArrowCircleUp}
        iconColor="orange.500"
        iconBg="orange.50"
        borderColor="orange.400"
        isLoading={isLoading}
        divergenciasCount={resumo.divergenciasSaidas}
        totalValue={resumo.totalSaidas}
        empresaStats={saidasStats}
        isSelectable
        isSelected={selectedFilters.includes('saidas')}
        isDisabled={disabledFilters.includes('saidas')}
        onClick={() => onFilterToggle?.('saidas')}
      />

      <KpiCard
        label="Servicos"
        value={resumo.totalServicos}
        helpText={formatCurrency(resumo.valorServicos)}
        icon={Package}
        iconColor="purple.500"
        iconBg="purple.50"
        borderColor="purple.400"
        isLoading={isLoading}
        divergenciasCount={resumo.divergenciasServicos}
        totalValue={resumo.totalServicos}
        empresaStats={servicosStats}
        isSelectable
        isSelected={selectedFilters.includes('servicos')}
        isDisabled={disabledFilters.includes('servicos')}
        onClick={() => onFilterToggle?.('servicos')}
      />

      <KpiCard
        label="Total Divergencias"
        value={divergencias.quantidade}
        helpText={hasDivergencias ? formatCurrency(divergencias.diferenca) : 'Sem divergencias'}
        icon={WarningCircle}
        iconColor={hasDivergencias ? 'red.500' : 'gray.400'}
        iconBg={hasDivergencias ? 'red.50' : 'gray.50'}
        borderColor={hasDivergencias ? 'red.400' : 'gray.300'}
        isLoading={isLoading}
        valueColor={hasDivergencias ? 'red.600' : 'gray.600'}
        isHighlight={hasDivergencias && !selectedFilters.includes('divergencias')}
        empresaStats={divergenciasStats}
        isSelectable
        isSelected={selectedFilters.includes('divergencias')}
        selectedColor="red"
        onClick={() => onFilterToggle?.('divergencias')}
      />
    </SimpleGrid>
  );
}
